import React, { useState, useEffect, forwardRef, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { useMutation } from '@tanstack/react-query';
import { useGlobal } from 'contexts/globalContext';
import 'draft-js/dist/Draft.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './draft-editor.scss';
import paletteIcon from 'assets/icons/new/palette.svg';

export type DraftEditorProps = {
	defaultValue?: string;
	onChange?: (value: string | null) => void;
	uploadRequestFunction?: ((data: FormData) => Promise<string>) | undefined;
	height?: number;
	simplified?: boolean;
	borderless?: boolean;
};

export const DraftEditor = forwardRef(
	(
		{ defaultValue, onChange, uploadRequestFunction, height, simplified, borderless }: DraftEditorProps,
		ref: Ref<Editor>
	) => {
		const { t } = useTranslation();
		const { toastRef } = useGlobal();

		const [editorState, setEditorState] = useState<EditorState | undefined>(undefined);
		const handleEditorStateChange = (editorState: EditorState) => {
			if (onChange) {
				const value = editorState && draftToHtml(convertToRaw(editorState.getCurrentContent()));
				onChange(value || null);
			}
			setEditorState(editorState);
		};

		useEffect(() => {
			if (defaultValue !== undefined) {
				const blocksFromHtml = htmlToDraft(defaultValue);
				const { contentBlocks, entityMap } = blocksFromHtml;
				const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
				setEditorState(EditorState.createWithContent(contentState));
			} else {
				setEditorState(EditorState.createEmpty());
			}
		}, [defaultValue]);

		const { mutateAsync: uploadImageMutateAsync } = uploadRequestFunction
			? useMutation(uploadRequestFunction)
			: { mutateAsync: () => new Promise<string>(() => {}) };

		const uploadCallback = uploadRequestFunction
			? (file: File): Promise<object> => {
					const formData = new FormData();
					formData.append('file', file);

					const maxFileSize = 1024 * 1024 * 3; // 3 mb

					return new Promise((resolve, reject) => {
						if (file.size > maxFileSize) {
							toastRef?.current?.show({
								severity: 'error',
								life: 3000,
								summary: t(`misc.error`),
								detail: t('misc.fileSizeExceeds', { count: 3 }),
							});
							reject(new Error('max file size'));
						}
						uploadImageMutateAsync(formData)
							.then((link) => resolve({ data: { link } }))
							.catch((err) => {
								if (err instanceof AxiosError) {
									const message = err.response?.data.message || err.message;
									switch (message) {
										case 'File too large':
											toastRef?.current?.show({
												severity: 'error',
												life: 3000,
												summary: t(`misc.error`),
												detail: t('misc.fileSizeExceeds', { count: 3 }),
											});
											break;
										case 'Invalid image format':
											toastRef?.current?.show({
												severity: 'error',
												life: 3000,
												summary: t(`misc.error`),
												detail: t('misc.fileInvalidFileType'),
											});
											break;
										default:
									}
								} else {
									toastRef?.current?.show({
										severity: 'error',
										life: 3000,
										summary: t(`misc.error`),
										detail: t('misc.fileUnexpectedError'),
									});
								}
								reject(new Error('unexpected upload error'));
							});
					});
			  }
			: undefined;

		const toolbarOptions = simplified
			? ['inline', 'fontSize', 'list', 'colorPicker', 'emoji', 'image']
			: [
					'inline',
					'blockType',
					'fontSize',
					'list',
					'textAlign',
					'colorPicker',
					'link',
					'emoji',
					'image',
					'remove',
					'history',
			  ];

		const toolbar = {
			options: toolbarOptions,
			inline: {
				options: simplified ? ['bold', 'italic'] : ['bold', 'italic', 'underline', 'strikethrough'],
			},
			list: {
				options: ['unordered', 'ordered'],
			},
			emoji: {
				popupClassName: 'popup-upload-in-draft-editor',
			},
			colorPicker: {
				icon: paletteIcon,
				popupClassName: 'popup-upload-in-draft-editor',
			},
			url: {
				popupClassName: 'popup-upload-in-draft-editor',
			},
			...(uploadCallback
				? {
						image: {
							uploadCallback,
							previewImage: true,
							alt: { present: true, mandatory: false },
							inputAccept: 'image/gif,image/jpeg,image/jpg,image/png',
						},
				  }
				: {
						image: {
							className: 'hidden',
							visible: false,
							urlEnabled: false,
							uploadEnabled: false,
						},
				  }),
			link: {
				popupClassName: 'popup-upload-in-draft-editor',
			},
		};

		const finalHeight = height || 13;
		return (
			<div
				className={`p-inputtext p-component draft-editor-container ${
					borderless ? 'border-none border-round-none shadow-none overflow-visible' : 'overflow-visible'
				} p-0 pb-2`}
			>
				<Editor
					ref={ref}
					toolbar={toolbar}
					uploadCallback={uploadCallback}
					wrapperClassName="editor-wrapper p-full pb-3 "
					editorClassName="editor px-3"
					toolbarClassName="border-none bg-transparent"
					onEditorStateChange={handleEditorStateChange}
					editorState={editorState}
					wrapperStyle={{ minHeight: `${finalHeight + 2}rem` }}
					editorStyle={{ minHeight: `${finalHeight}rem` }}
					stripPastedStyles
				/>
			</div>
		);
	}
);
